import { useMutationObserver } from '@vueuse/core';
import { type Ref, onMounted, watchEffect } from 'vue';

const COMBOBOX_INPUT_SELECTOR = '.ant-select-selection-search-input';

type ComboboxInputNameType = {
  ariaLabel?: string | null;
  ariaLabelledby?: string | null;
  hasVisibleLabel?: boolean;
};

function setOrRemove(el: Element, attr: string, value?: string | null) {
  if (value) el.setAttribute(attr, value);
  else el.removeAttribute(attr);
}

/**
 * Puts `aria-label` / `aria-labelledby` on the combobox input that Ant Design's
 * Select renders, which is the element that receives focus. Ant forwards these
 * attributes to its wrapper div instead (ant-design-vue 4.2.6), so a select
 * named on the component is still announced as an unnamed combobox.
 *
 * A visible label (`hasVisibleLabel`, from the component's `formLabel`) already
 * names the input through `<label for>`, so it wins: the aria props are not
 * applied and passing both logs a warning.
 *
 * Re-applies when Ant replaces the input (for example when `showSearch` toggles)
 * and whenever the name changes. Vue does not patch attributes it did not set,
 * so ordinary re-renders keep the name.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/combobox/ (2026-10-05)
 */
export function useComboboxInputName(
  wrapper: Ref<HTMLElement | null>,
  getName: () => ComboboxInputNameType,
) {
  function applyName() {
    const input = wrapper.value?.querySelector(COMBOBOX_INPUT_SELECTOR);
    if (!input) return;
    const { ariaLabel, ariaLabelledby, hasVisibleLabel } = getName();
    setOrRemove(input, 'aria-label', hasVisibleLabel ? null : ariaLabel);
    setOrRemove(
      input,
      'aria-labelledby',
      hasVisibleLabel ? null : ariaLabelledby,
    );
  }

  onMounted(() => {
    const { ariaLabel, ariaLabelledby, hasVisibleLabel } = getName();
    if (hasVisibleLabel && (ariaLabel || ariaLabelledby)) {
      console.warn(
        'ariaLabel/ariaLabelledby are ignored when formLabel is set; the visible label already names the select',
      );
    }
  });
  useMutationObserver(wrapper, applyName, { childList: true, subtree: true });
  watchEffect(applyName, { flush: 'post' });
}
